import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiClientError, UNAUTHORIZED_EVENT } from './api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  cnicMasked?: string | null;
  kycStatus: 'pending' | 'verified' | 'failed';
  createdAt: string;
  lastLoginAt?: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signup: (input: SignupInput) => Promise<void>;
  signin: (email: string, password: string) => Promise<void>;
  signout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SignupInput {
  name: string;
  email: string;
  phone?: string;
  cnic?: string;
  password: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ user: AuthUser }>('/auth/me');
      setUser(r.user);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        setUser(null);
      } else {
        // network down — keep stale user (don't kick them out on flaky connection)
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any non-/auth request returning 401 means the cookie expired or was
  // invalidated server-side. Clear the user — the route guard takes over and
  // redirects to /auth/signin, instead of the dashboard silently flipping
  // to fixture/demo data.
  useEffect(() => {
    const onUnauth = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauth);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauth);
  }, []);

  const signup = async (input: SignupInput) => {
    const r = await api.post<{ user: AuthUser }>('/auth/signup', input);
    setUser(r.user);
  };

  const signin = async (email: string, password: string) => {
    const r = await api.post<{ user: AuthUser }>('/auth/signin', { email, password });
    setUser(r.user);
  };

  const signout = async () => {
    try {
      await api.post('/auth/signout');
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signup, signin, signout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Deployment-level auth settings, fetched before anyone is signed in.
 * `signupEnabled` defaults to false until the API answers, so a closed
 * deployment never flashes a "Create account" link.
 */
export const useAuthConfig = (): { signupEnabled: boolean; loaded: boolean } => {
  const [cfg, setCfg] = useState({ signupEnabled: false, loaded: false });
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ signupEnabled: boolean }>('/auth/config')
      .then((r) => !cancelled && setCfg({ signupEnabled: !!r.signupEnabled, loaded: true }))
      .catch(() => !cancelled && setCfg({ signupEnabled: false, loaded: true }));
    return () => {
      cancelled = true;
    };
  }, []);
  return cfg;
};
